import {
	QueryFunctionContext,
	useInfiniteQuery,
	useMutation,
	useQuery,
} from '@tanstack/react-query';
import {
	createUser,
	deleteUser,
	getAllAdminLogs,
	getAllAdminUsers,
	getAllUsers,
	getMetaLogs,
	getRoles,
	getRolesHierarchy,
	getUsersBySite,
	sendTemporaryPassword,
	updateUser,
} from '@/api/users';
import { CreateUserRequest, RoleResponse, UpdateUserRequest } from '@/api/users/types';
import useToastMessage from '@/shared/context/toast-message-context';
import { CustomerUsersQueryParams } from '@/api/customers/types';
import { ApiError } from '@/shared/types/api-errors';
import { AxiosError } from 'axios';
import {
	CreateMutationFnParams,
	UseMutationQueryParams,
	UpdateMutationFnParams,
	UseQueryParams,
} from './types';

const userKeys = {
	allAdmins: () => ['all_admins'] as const,
	allUsers: () => ['all_users'] as const,
	allUsersBySite: () => ['all_users_by_site'] as const,
	allRoles: () => ['all_roles'] as const,
	rolesHierarchy: () => ['roles_hierarchy'] as const,
};

interface UseAdminUsersParams {
	queryParams?: {
		limit?: number;
		search?: string;
	};
}

interface UseLogsParams {
	options?: {
		enabled?: boolean;
	};
	queryParams?: {
		limit?: number;
		search?: string;
		siteId?: string;
		customerId?: string;
		locationId?: string;
		fromDate: string;
		toDate: string;
	};
}

interface UseMetaLogsParams {
	options?: {
		enabled?: boolean;
	};
	queryParams?: {
		limit?: number;
		search?: string;
		siteId?: string;
		customerId?: string;
		locationId?: string;
		fromDate: string;
		toDate: string;
	};
}

export const useMetaDataLogs = ({ queryParams, options }: UseMetaLogsParams) => {
	const queryKey = [...userKeys.allAdmins(), queryParams];
	const query = useQuery({
		queryKey,
		queryFn: () => getMetaLogs(queryParams ?? {}),
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		enabled: options ? options.enabled : true,
	});

	return { queryKey, query };
};

export const useLogs = ({ queryParams, options }: UseLogsParams) => {
	const queryKey = [...userKeys.allAdmins(), queryParams];
	const query = useInfiniteQuery({
		queryKey,
		queryFn: ({
			pageParam = 1,
		}: QueryFunctionContext<(string | { search?: string } | undefined)[], number>) =>
			getAllAdminLogs({ ...queryParams, page: pageParam }),
		getNextPageParam: ({ page, totalPages }) => (page === totalPages ? undefined : page + 1),
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		enabled: options ? options.enabled : true,
	});

	return { queryKey, query };
};

export const useAdminUsers = ({ queryParams }: UseAdminUsersParams) => {
	const queryKey = [...userKeys.allAdmins(), queryParams];
	const query = useInfiniteQuery({
		queryKey,
		queryFn: ({
			pageParam = 1,
		}: QueryFunctionContext<(string | { search?: string } | undefined)[], number>) =>
			getAllAdminUsers({ ...queryParams, page: pageParam }),
		getNextPageParam: ({ page, totalPages }) => (page === totalPages ? undefined : page + 1),
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

	return { queryKey, query };
};

export const useUsersBySite = (siteId?: string, queryParams?: CustomerUsersQueryParams) => {
	const queryKey = [...userKeys.allUsersBySite(), siteId, queryParams];
	const query = useInfiniteQuery({
		queryKey,
		queryFn: ({
			pageParam = 1,
		}: QueryFunctionContext<(string | { search?: string } | undefined)[], number>) =>
			getUsersBySite(siteId!, { ...queryParams, page: pageParam }),
		getNextPageParam: ({ page, totalPages }) => (page === totalPages ? undefined : page + 1),
		enabled: !!siteId,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

	return { queryKey, query };
};

export const useRoles = (useQueryParams?: UseQueryParams) =>
	useQuery({
		queryKey: userKeys.allRoles(),
		queryFn: () => getRoles(),
		select: (roles: RoleResponse[]) => roles,
		enabled: useQueryParams?.enabled,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

export const useRolesHierarchy = ({ enabled }: UseQueryParams = {}) =>
	useQuery({
		queryKey: userKeys.rolesHierarchy(),
		queryFn: () => getRolesHierarchy(),
		enabled,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

export const useCreateUser = (mutationQueryParams: UseMutationQueryParams) => {
	const { dispatch } = useToastMessage();
	return useMutation({
		mutationFn: ({ request }: CreateMutationFnParams<CreateUserRequest>) => createUser(request),
		...mutationQueryParams,
		onSuccess: async () => {
			await mutationQueryParams?.onSuccess?.();
			dispatch({
				type: 'success',
				content: 'User successfully created',
			});
		},
	});
};

export const useUpdateUser = (mutationQueryParams: UseMutationQueryParams) => {
	const { dispatch } = useToastMessage();
	return useMutation({
		mutationFn: ({ id, request }: UpdateMutationFnParams<UpdateUserRequest>) =>
			updateUser(id, request),
		...mutationQueryParams,
		onSuccess: async () => {
			await mutationQueryParams?.onSuccess?.();
			dispatch({
				type: 'success',
				content: 'User successfully updated',
			});
		},
	});
};

export const useDeleteUser = (mutationQueryParams: UseMutationQueryParams) => {
	const { dispatch } = useToastMessage();
	return useMutation({
		mutationFn: (id: string) => deleteUser(id),
		onSuccess: async () => {
			await mutationQueryParams?.onSuccess?.();
			dispatch({
				type: 'success',
				content: 'User successfully deleted',
			});
		},
	});
};

export const useSendTemporaryPassword = (mutationQueryParams?: UseMutationQueryParams) => {
	const { dispatch } = useToastMessage();
	return useMutation({
		mutationFn: (id: string) => sendTemporaryPassword(id),
		...mutationQueryParams,
		onSuccess: async () => {
			await mutationQueryParams?.onSuccess?.();
			dispatch({
				type: 'success',
				content: 'Temporary password emailed to the user',
			});
		},
		onError: async (error: AxiosError<ApiError>) => {
			const message = error.response?.data?.message || 'Could not send temporary password';
			dispatch({
				type: 'error',
				content: message,
			});
			await mutationQueryParams?.onError?.(error);
		},
	});
};

interface UseUsersParams {
	queryParams?: {
		limit?: number;
		search?: string;
		roleId?: string;
		siteId?: string;
	};
}

export const useUsers = ({ queryParams }: UseUsersParams) => {
	const queryKey = [...userKeys.allUsers(), queryParams];
	const query = useInfiniteQuery({
		queryKey,
		queryFn: ({
			pageParam = 1,
		}: QueryFunctionContext<(string | { search?: string } | undefined)[], number>) =>
			getAllUsers({ ...queryParams, page: pageParam }),
		getNextPageParam: ({ page, totalPages }) => (page === totalPages ? undefined : page + 1),
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

	return { queryKey, query };
};
